/**
 * 코어 밸런스 — 초기 재화·등급 색·전투/경제 공용 상수 (global.tsv·shop.tsv).
 * 수치는 TSV에서 수정 후 `npm run export:data` — 이 파일은 globalRepo/shop JSON 재구성만 한다.
 * global.tsv는 long format(id/type/value) → {@link globalRepo}로 조회(키 상수 {@link K}, 오타=컴파일 에러).
 * 신규 도메인 상수는 여기 비대화 금지 — `data/balance/<domain>.ts` 자기 파일 + 배럴 1줄 (§5 P3).
 */
import { globalRepo as r, K } from "../globalRepo.js";
import shopJson from "../generated/shop.json";
import type { ModuleRarity } from "../../modules/ProjectileModule.js";

// shop.tsv 는 설정 묶음 1행 → 객체 1개 배열. [0]으로 접근 (wide format 유지).
const s = shopJson[0]!;

/** 신규 게임 초기 재화 (global.tsv). */
export const INITIAL_GOLD = r.getInt(K.initialGold);
export const INITIAL_DIAMOND = r.getInt(K.initialDiamond);

/**
 * 전체 사운드 출력 배수(마스터 게인, global.tsv). def.volume 가중치가 전반적으로 작아 그대로면
 * 작게 들리므로 일괄 증폭(상대 밸런스는 def.volume 유지). SoundController에 주입 → VolumeMixer master.
 */
export const SOUND_MASTER_GAIN = r.getFloat(K.soundMasterGain);

/**
 * 등급별 표시 색 — global.tsv가 SSoT (코드 하드코딩 금지). uiTokens `RARITY_COLOR`가 이를 재노출,
 * 도감/인벤토리/팔레트/유물/보상 팝업이 공유한다.
 */
export const RARITY_COLORS: Record<ModuleRarity, string> = {
  common: r.getString(K.rarityColorCommon),
  rare: r.getString(K.rarityColorRare),
  epic: r.getString(K.rarityColorEpic),
  legendary: r.getString(K.rarityColorLegendary),
  transcendent: r.getString(K.rarityColorTranscendent),
};

export const MODULE_BRICK_BALANCE = {
  baseDamage: r.getInt(K.baseDamage),
  baseProjectileSpeed: r.getInt(K.baseProjectileSpeed),
  fireIntervalMs: r.getInt(K.fireIntervalMs),
  brickBaseHp: r.getInt(K.brickBaseHp),
  brickHpGrowth: r.getFloat(K.brickHpGrowth),
  brickBaseGold: r.getInt(K.brickBaseGold),
  brickGoldGrowth: r.getFloat(K.brickGoldGrowth),
  /** 골드 모듈 상자 가격 = base + 개봉수 × increase (shop.tsv). */
  goldBoxBaseCost: s.goldBoxBaseCost,
  goldBoxCostIncrease: s.goldBoxCostIncrease,
  guaranteedBoxOpenCount: s.guaranteedBoxOpenCount,
  /** 10연 구매 지불 단가 배수 (shop.tsv) — 10× 단가에서 할인(기본 9 = 1개분 할인). cost = goldBoxCost(count) × this. */
  goldBox10PayUnits: s.goldBox10PayUnits,
  /** 누적 천장 주기 (shop.tsv) — 1개·10개 구매 공용 카운터가 이 횟수째마다 pityGuaranteeRarity 이상 확정. */
  pityInterval: s.pityInterval,
  /** 천장 보장 최소 등급 (shop.tsv) — 보장 pull에서 추첨 등급이 이 미만이면 이 등급 이상으로 상향. */
  pityGuaranteeRarity: s.pityGuaranteeRarity as ModuleRarity,
  /** 다이아 강화 상자 가격 (고정, shop.tsv). */
  diamondBoxCost: s.diamondBoxCost,
  /** 다음 환생 요구 챕터를 초기 조건으로 되돌리는 상점 상품 가격 (다이아, shop.tsv). */
  rebirthConditionResetDiamondCost: s.rebirthConditionResetDiamondCost,
  rebirthRequiredChapter: r.getInt(K.rebirthRequiredChapter),
  /**
   * 환생 가능 조건 챕터 산식 — `{rebirthCount}` 플레이스홀더 (global.tsv). idleblade식 누진:
   * `({rebirthCount} + 1) * 10` → 0회=10·1회=20·2회=30. evaluateFormula로 평가 (RebirthController.canRebirth).
   * (구 rebirthRequiredChapter 고정값을 대체 — 미사용으로 남겨둠.)
   */
  rebirthChapterExpr: r.getString(K.rebirthChapterExpr),
  /**
   * 환생 실행 시 획득 환생 포인트 산식 — `{bestChapter}` 플레이스홀더 (global.tsv). 챕터 비례.
   * evaluateFormula는 floor 미지원이라 코드(RebirthController.gainedPoints)에서 Math.floor로 래핑 + 포인트보너스 가산.
   */
  rebirthPointExpr: r.getString(K.rebirthPointExpr),
  /** 환생 영구 강화 1포인트당 기본 피해 가산(가법). baseDamage = baseDamage + permanentDamageBonus. */
  permanentDamagePerPoint: r.getFloat(K.permanentDamagePerPoint),
  /** 모듈 공통 레벨 효과 — 레벨당 피해 배율 가산. */
  moduleLevelDamageBonus: r.getFloat(K.moduleLevelDamageBonus),
  /** 숙련도 XP — 경로 기여 모듈이 발동 1회 시 획득. */
  moduleMasteryActivationXp: r.getInt(K.moduleMasteryActivationXp),
  /** 숙련도 XP — 경로 기여 모듈 발사체가 타격 1회 시 획득. */
  moduleMasteryHitXp: r.getInt(K.moduleMasteryHitXp),
  /** 숙련도 XP — 경로 기여 모듈 발사체가 처치 1회 시 획득. */
  moduleMasteryKillXp: r.getInt(K.moduleMasteryKillXp),
  /** 폭발 모듈 Lv1 기본 반경(px) — global.tsv. explosionRadius = base + perLevel×(level-1). */
  explosionBaseRadius: r.getInt(K.explosionBaseRadius),
  /** 폭발 모듈 레벨당 반경 증가(px) — global.tsv. */
  explosionRadiusPerLevel: r.getInt(K.explosionRadiusPerLevel),
  /** 연사(burst) 모듈 2번째 발사 지연(ms) — global.tsv. */
  burstDelayMs: r.getInt(K.burstDelayMs),
  /** 크리티컬 모듈 Lv1 발동 확률(0~1) — global.tsv. critChance = base + perLevel×(level-1). */
  critBaseChance: r.getFloat(K.critBaseChance),
  /** 크리티컬 모듈 레벨당 확률 증가 — global.tsv. */
  critChancePerLevel: r.getFloat(K.critChancePerLevel),
  /** 크리티컬 발동 시 피해 배수 — global.tsv. */
  critMultiplier: r.getFloat(K.critMultiplier),
  /** 크리티컬 발동 시 공 근처에 띄우는 텍스트 — global.tsv (localize 제외). */
  critText: r.getString(K.critText),
  /** critical! 텍스트 색(hex) — global.tsv. floatingText color로 직접 전달. */
  critTextColor: r.getString(K.critTextColor),
  /** 크리티컬 시 폭발 모듈 반경 배수 — global.tsv. explosionRadius × 이 값 (폭발 모듈일 때만). */
  critExplosionRadiusMul: r.getFloat(K.critExplosionRadiusMul),
  /** 크리티컬 폭발 링 색(hex) — global.tsv. 일반 폭발과 시각 차별화. hexToTint로 변환해 explosionRing color에 전달. */
  critExplosionColor: r.getString(K.critExplosionColor),
  /** 크리티컬 시 관통 횟수 가산 — global.tsv. pierceCount + 이 값 (관통 모듈일 때만). */
  critPierceBonus: r.getInt(K.critPierceBonus),
  /** 크리티컬 시 연쇄 타겟 가산 — global.tsv. chainCount + 이 값 (연쇄 모듈일 때만). */
  critChainBonus: r.getInt(K.critChainBonus),
  /** 크리티컬 시 분열 추가탄 가산 — global.tsv. splitCount + 이 값 (분열 모듈일 때만). */
  critSplitBonus: r.getInt(K.critSplitBonus),
  /** 크리티컬 시 탐욕 골드 배수 — global.tsv. goldMultiplier × 이 값 (탐욕 모듈일 때만). */
  critGreedMul: r.getFloat(K.critGreedMul),
  /** 벽돌 피격 반동 세기 배수(비크리티컬) — global.tsv. playImpactShake step에 곱(현재 기준 0.8 = 80%). */
  impactShakeScale: r.getFloat(K.impactShakeScale),
  /** 벽돌 피격 반동 세기 배수(크리티컬) — global.tsv. playImpactShake step에 곱(현재 기준 1.2 = 120%). */
  impactShakeCritScale: r.getFloat(K.impactShakeCritScale),
  /** 크리티컬 파괴 시 조각 분출 에너지 배수 — global.tsv. emitBrickShatter energyMul(조각이 더 세게·멀리 튄다). */
  critShatterEnergyMul: r.getFloat(K.critShatterEnergyMul),
  /** 크리티컬 타격 스파크 크기 배수 — global.tsv. spark startScale/endScale에 곱(크리티컬 색은 critTextColor 재사용). */
  critSparkScale: r.getFloat(K.critSparkScale),
  /** 크리티컬 데미지 숫자 폰트 크기 — global.tsv. floatingText fontSize(크리티컬만 — 일반은 기본 11px). */
  critDamageTextSize: r.getString(K.critDamageTextSize),
  /** 탐욕(greed) 모듈 Lv1 골드 배수 — global.tsv. goldMultiplier = base + perLevel×(level-1). */
  greedBaseMultiplier: r.getFloat(K.greedBaseMultiplier),
  /** 탐욕 모듈 레벨당 골드 배수 증가 — global.tsv. */
  greedPerLevel: r.getFloat(K.greedPerLevel),
  /** 신속(speed) 모듈 Lv1 발사체 속도 배수 — global.tsv. speedMultiplier = base + perLevel×(level-1). */
  speedModuleBase: r.getFloat(K.speedModuleBase),
  /** 신속 모듈 레벨당 속도 배수 증가 — global.tsv. */
  speedModulePerLevel: r.getFloat(K.speedModulePerLevel),
  /** 모듈 레벨업 1회 강화석 비용 base (shop.tsv). cost = base × growth^level (강화석 전용, 골드 미사용). */
  upgradeStoneBase: s.upgradeStoneBase,
  /** 모듈 레벨업 강화석 비용 지수 누진 계수 (shop.tsv). 초반 저렴·후반 체감 (예: 1.12). */
  upgradeStoneCostGrowth: s.upgradeStoneCostGrowth,
  /** 환생 포인트 1 → 강화석 N 교환 비율 (shop.tsv, 강화석 수급 P1). */
  stonePerRebirthPoint: s.stonePerRebirthPoint,
  /** 던전 전투 제한 시간(초, 고정) — 초과 시 실패 + 일반 챕터 복귀 (global.tsv). */
  dungeonTimeLimitSec: r.getInt(K.dungeonTimeLimitSec),
  /** 모듈 융합 2회째부터 소모하는 다이아(고정, 1회째는 광고) — global.tsv. ModuleFusionController에 주입. */
  fusionDiamondCost: r.getInt(K.fusionDiamondCost),
} as const;

/** 발사 간격 하한(ms) — fireRate 강화 누적 시 과도한 단축 방지 클램프. */
export const MIN_FIRE_INTERVAL_MS = 150;

/**
 * 발사 속도 상한(px/s) — speedMultiplier(유물/신속 모듈) 곱셈 누적이 극단으로 커질 때 수치 안정 상한.
 * baseProjectileSpeed(360)의 6배. 연속 충돌(projectileSweep)이 터널링을 근본 차단하는 것과 별개의 방어 레이어.
 * MIN_FIRE_INTERVAL_MS와 동형 — 디자이너 빈도 조정값이 아닌 안전 가드라 코드 상수(데이터 외부화 제외).
 */
export const MAX_PROJECTILE_SPEED = 360 * 6;
